/**
 * SMEEvolutionView — /sme/monthly/trends
 * Monthly evolution trend view + YTD + Trailing 12 months + Target vs Actual.
 * Housing shell standard. Institutional in presentation.
 */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { RefreshCw, TrendingDown, TrendingUp, Minus, Target, AlertTriangle, CheckCircle2, BarChart3 } from "lucide-react";
import { useSMEPeriodic } from "@/hooks/useSMEPeriodic";
import PortfolioIntelligenceTab from "@/components/PortfolioIntelligenceTab";

import { PageHeader, SectionCard, MetricCard, CTAButton } from "@/components/ds";
// ── Decision badge ────────────────────────────────────────────────────────────
const DECISION_COLORS = {
  ready_for_management_review: "bg-emerald-100 text-emerald-700 border-emerald-200",
  ready_for_external_reporting: "bg-teal-100 text-teal-700 border-teal-200",
  proceed_with_conditions: "bg-amber-100 text-amber-700 border-amber-200",
  technical_assistance_required: "bg-orange-100 text-orange-700 border-orange-200",
  data_improvement_required: "bg-red-100 text-red-700 border-red-200",
  proceed_to_credit_committee: "bg-emerald-100 text-emerald-700 border-emerald-200",
  not_financeable: "bg-red-100 text-red-700 border-red-200",
};

function DecisionBadge({ decision }) {
  if (!decision?.outcome) return null;
  const cls = DECISION_COLORS[decision.outcome] || "bg-slate-100 text-slate-600 border-slate-200";
  return (
    <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${cls}`}
      data-testid={`decision-badge-${decision.outcome}`}>
      {decision.outcome_label}
    </span>
  );
}

// ── Anomaly severity badge ────────────────────────────────────────────────────
const SEV_COLORS = { critical: "text-red-600 bg-red-50", high: "text-amber-600 bg-amber-50", medium: "text-orange-500 bg-orange-50", low: "text-slate-500 bg-slate-50" };

// ── Trend bar chart (using colored rects, no chart library) ──────────────────
function TrendBar({ label, scope1, scope2, scope3, maxTotal, isLocked }) {
  const total = (scope1 || 0) + (scope2 || 0) + (scope3 || 0);
  const pct   = total / Math.max(maxTotal, 0.001);
  const COLORS = { scope1: "bg-amber-500", scope2: "bg-teal-500", scope3: "bg-blue-400" };
  const s1w = maxTotal > 0 ? (scope1 / maxTotal) * 100 : 0;
  const s2w = maxTotal > 0 ? (scope2 / maxTotal) * 100 : 0;
  const s3w = maxTotal > 0 ? (scope3 / maxTotal) * 100 : 0;

  return (
    <div className="flex items-end gap-1.5" style={{ height: 80 }}>
      <div className="flex-1 flex flex-col justify-end" title={`${label}: ${total.toFixed(3)} tCO2e`}>
        <div className="w-full flex flex-col rounded-t overflow-hidden" style={{ height: `${pct * 72}px` }}>
          <div className={`${COLORS.scope3} flex-none`} style={{ height: `${s3w * pct}%` }} />
          <div className={`${COLORS.scope2} flex-none`} style={{ height: `${s2w * pct}%` }} />
          <div className={`${COLORS.scope1} flex-none`} style={{ height: `${s1w * pct}%` }} />
        </div>
        <p className="text-xs text-slate-400 text-center mt-1 leading-tight">{label?.slice(5)}</p>
        {isLocked && <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 mx-auto" />}
      </div>
    </div>
  );
}

// ── Delta badge ───────────────────────────────────────────────────────────────
function DeltaBadge({ delta }) {
  if (!delta || !delta.total_tco2e) return <span className="text-slate-300 text-xs">—</span>;
  const pct = delta.total_pct || 0;
  const up  = pct > 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-bold ${up ? "text-red-600" : pct < 0 ? "text-emerald-600" : "text-slate-400"}`}>
      {up ? <TrendingUp size={9} /> : pct < 0 ? <TrendingDown size={9} /> : <Minus size={9} />}
      {pct > 0 ? "+" : ""}{pct.toFixed(1)}%
    </span>
  );
}

export default function SMEEvolutionView({ defaultView = "trend" }) {
  const navigate = useNavigate();
  const { evolution, ytdData, trailing12, loading, selectedYear, setSelectedYear } = useSMEPeriodic();
  const [view, setView] = useState(defaultView);  // trend | ytd | t12 | target

  if (loading) return <div className="flex items-center justify-center min-h-[40vh]"><RefreshCw size={18} className="animate-spin text-teal-500" /></div>;

  const maxTotal = Math.max(...evolution.map(p => (p.total_tco2e || 0)), 0.001);

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <PageHeader
        label="MONTHLY TRACKING"
        title="Emissions Trend"
        subtitle="Monthly evolution · YTD · Trailing 12 months · Target vs actual"
        backUrl="/sme/overview"
        backLabel="SME Workspace"
      />

        {/* View selector — matches housing tab pattern */}
        <div className="flex gap-1 mb-5 p-1 bg-white border border-slate-200 rounded-xl overflow-x-auto">
          {[
            { id: "trend",     label: "Monthly Trend"   },
            { id: "ytd",       label: "Year to Date"    },
            { id: "t12",       label: "Trailing 12M"    },
            { id: "portfolio", label: "Portfolio Intel" },
            { id: "decision",  label: "Decisions"       },
            { id: "anomalies", label: "Anomalies"       },
            { id: "target",    label: "Target vs Actual"},
          ].map(v => (
            <button key={v.id} onClick={() => setView(v.id)}
              className={`px-4 py-2 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors ${view === v.id ? "bg-[#0F172A] text-white" : "text-slate-500 hover:text-slate-700 hover:bg-slate-50"}`}
              data-testid={`view-${v.id}`}>
              {v.label}
            </button>
          ))}
        </div>

        {evolution.length === 0 && (
          <SectionCard>
            <div className="py-10 text-center">
              <Target size={24} className="text-slate-300 mx-auto mb-3" />
              <p className="text-sm font-semibold text-slate-600">No monthly data yet</p>
              <p className="text-xs text-slate-400 mb-4">Enter your first month's data to start tracking.</p>
              <CTAButton variant="action" onClick={() => navigate("/sme/monthly/inputs")}>Enter Monthly Data →</CTAButton>
            </div>
          </SectionCard>
        )}

        {evolution.length > 0 && view === "trend" && (
          <div className="space-y-6">
            {/* Bar chart */}
            <SectionCard title="Monthly Emissions (tCO₂e)" testid="trend-chart" action={
              <div className="flex gap-3 text-xs">
                {[["bg-amber-500","Scope 1"],["bg-teal-500","Scope 2"],["bg-blue-400","Scope 3"]].map(([c,l]) => (
                  <span key={l} className="flex items-center gap-1"><span className={`w-2 h-2 rounded-sm ${c}`}/>{l}</span>
                ))}
              </div>
            }>
              <div className="flex items-end gap-1 min-h-[90px]">
                {evolution.map(p => (
                  <TrendBar key={p.period_label} label={p.period_label}
                    scope1={p.scope1_tco2e} scope2={p.scope2_tco2e} scope3={p.scope3_tco2e}
                    maxTotal={maxTotal} isLocked={p.is_locked} />
                ))}
              </div>
            </SectionCard>

            {/* Monthly table */}
            <SectionCard title="Period Detail" noPadding>
              <table className="w-full text-xs">
                <thead><tr className="border-b border-slate-100 bg-slate-50">
                  {["Period","Total","Scope 1","Scope 2","Scope 3","vs Prior","Status"].map(h => (
                    <th key={h} className="text-left text-xs text-slate-500 px-4 py-3 font-semibold">{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {evolution.slice().reverse().map(p => (
                    <tr key={p.period_label} className="border-b border-slate-50 hover:bg-slate-50/50"
                      data-testid={`period-row-${p.period_label}`}>
                      <td className="px-4 py-2.5 font-bold text-slate-900">{p.period_label}</td>
                      <td className="px-4 py-2.5 font-bold text-teal-700">{(p.total_tco2e||0).toFixed(3)}</td>
                      <td className="px-4 py-2.5 text-slate-600">{(p.scope1_tco2e||0).toFixed(3)}</td>
                      <td className="px-4 py-2.5 text-slate-600">{(p.scope2_tco2e||0).toFixed(3)}</td>
                      <td className="px-4 py-2.5 text-slate-600">{(p.scope3_tco2e||0).toFixed(3)}</td>
                      <td className="px-4 py-2.5"><DeltaBadge delta={p.delta_vs_prior} /></td>
                      <td className="px-4 py-2.5">
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${p.is_locked ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                          {p.is_locked ? "Locked" : p.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </SectionCard>
          </div>
        )}

        {view === "ytd" && ytdData && (
          <div className="space-y-6">
            <div className="grid grid-cols-3 gap-4">
              <MetricCard label={`YTD Total ${selectedYear}`}   value={`${(ytdData.ytd_total_tco2e||0).toFixed(3)} tCO₂e`}  color="text-teal-700" />
              <MetricCard label={`Scope 1 ${selectedYear}`}     value={`${(ytdData.ytd_scope1_tco2e||0).toFixed(3)} tCO₂e`} color="text-amber-700" />
              <MetricCard label={`Scope 2 ${selectedYear}`}     value={`${(ytdData.ytd_scope2_tco2e||0).toFixed(3)} tCO₂e`} color="text-teal-600" />
            </div>
            {ytdData.annual_target_tco2e && (
              <SectionCard title="Target Progress" testid="target-progress">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs text-slate-600">Annual target: {ytdData.annual_target_tco2e} tCO₂e</p>
                  <p className="text-xs font-bold text-slate-700">{ytdData.target_pct_achieved}% used</p>
                </div>
                <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full transition-all ${
                    ytdData.target_pct_achieved > 90 ? "bg-red-500" : ytdData.target_pct_achieved > 70 ? "bg-amber-500" : "bg-emerald-500"
                  }`} style={{ width: `${Math.min(ytdData.target_pct_achieved || 0, 100)}%` }} />
                </div>
              </SectionCard>
            )}
            {/* Monthly breakdown */}
            <SectionCard title={`Monthly Breakdown — ${selectedYear}`} noPadding>
              <table className="w-full text-xs">
                <thead><tr className="border-b border-slate-100 bg-slate-50">
                  {["Month","Total","Cumulative YTD"].map(h => (
                    <th key={h} className="text-left text-xs text-slate-500 px-4 py-3 font-semibold">{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {(ytdData.monthly_breakdown || []).map((m, i) => {
                    const cumulative = (ytdData.monthly_breakdown || []).slice(0,i+1).reduce((s,x) => s + (x.total_tco2e||0), 0);
                    return (
                      <tr key={m.period_label} className="border-b border-slate-50">
                        <td className="px-4 py-2">{m.period_label}</td>
                        <td className="px-4 py-2 font-bold text-teal-700">{(m.total_tco2e||0).toFixed(3)}</td>
                        <td className="px-4 py-2 text-slate-600">{cumulative.toFixed(3)} tCO2e</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </SectionCard>
          </div>
        )}

        {view === "t12" && trailing12 && (
          <div className="space-y-6">
            <div className="grid grid-cols-4 gap-4">
              <MetricCard label="T12M Total" value={`${(trailing12.t12_total_tco2e||0).toFixed(2)} tCO₂e`} color="text-teal-700" />
              <MetricCard label="Scope 1"    value={(trailing12.t12_scope1_tco2e||0).toFixed(2)}           color="text-amber-700" />
              <MetricCard label="Scope 2"    value={(trailing12.t12_scope2_tco2e||0).toFixed(2)}           color="text-teal-600" />
              <MetricCard label="Scope 3"    value={(trailing12.t12_scope3_tco2e||0).toFixed(2)}           color="text-blue-600" />
            </div>
            <p className="text-xs text-slate-400 italic text-center">Trailing 12 months ({trailing12.periods_included} periods). Includes locked and unlocked periods.</p>
          </div>
        )}

        {view === "portfolio" && (
          <PortfolioIntelligenceTab workspace="sme" mode="operational" />
        )}

        {view === "decision" && (
          <div className="space-y-4">
          <SectionCard
            title="Decision Intelligence — Per Period"
            subtitle="Rule-based recommendations from confidence score, anomalies, trajectory, and target alignment"
            testid="decision-view"
            noPadding
          >
              <table className="w-full text-xs">
                <thead><tr className="bg-slate-50 border-b border-slate-100">
                  {["Period","Total tCO₂e","Recommendation","Confidence","Trajectory","Mode"].map(h => (
                    <th key={h} className="text-left text-xs text-slate-500 px-4 py-3 font-semibold">{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {evolution.slice().reverse().map(p => {
                    const dec = p.decision || {};
                    return (
                      <tr key={p.period_label} className="border-b border-slate-50 hover:bg-slate-50/40">
                        <td className="px-4 py-2.5 font-bold text-slate-900">{p.period_label}</td>
                        <td className="px-4 py-2.5 font-bold text-teal-700">{(p.total_tco2e||0).toFixed(3)}</td>
                        <td className="px-4 py-2.5"><DecisionBadge decision={dec} /></td>
                        <td className="px-4 py-2.5">
                          <span className="text-xs font-bold text-slate-600">{dec.confidence_score || "—"}/100</span>
                          {dec.confidence_tier && (
                            <span className="ml-1 text-xs text-slate-400">({dec.confidence_tier?.replace(/_/g," ")})</span>
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-xs text-slate-500 capitalize">{(p.trajectory || "—").replace(/_/g," ")}</td>
                        <td className="px-4 py-2.5 text-xs text-slate-400 capitalize">{dec.mode || "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
          </SectionCard>
            {evolution.length > 0 && evolution[evolution.length-1]?.decision?.conditions?.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-4">
                <p className="text-xs font-semibold text-amber-700 mb-2">Latest Period — Conditions to Address</p>
                {evolution[evolution.length-1].decision.conditions.map((c, i) => (
                  <p key={i} className="text-xs text-amber-700 mb-1">→ {c}</p>
                ))}
              </div>
            )}
          </div>
        )}

        {view === "anomalies" && (
          <div className="space-y-4" data-testid="anomalies-view">
            {evolution.filter(p => (p.anomaly_flags || []).length > 0).length === 0 ? (
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-8 text-center">
                <CheckCircle2 size={24} className="text-emerald-400 mx-auto mb-3" />
                <p className="text-sm font-bold text-emerald-700">No anomalies detected</p>
                <p className="text-xs text-emerald-600 mt-1">All periods are within normal variance thresholds.</p>
              </div>
            ) : (
              evolution.slice().reverse().filter(p => (p.anomaly_flags || []).length > 0).map(p => (
                <div key={p.period_label} className="bg-white border border-slate-200 rounded-xl overflow-hidden"
                  data-testid={`anomaly-period-${p.period_label}`}>
                  <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-100 flex items-center justify-between">
                    <p className="text-xs font-bold text-slate-900">{p.period_label} — {(p.total_tco2e||0).toFixed(3)} tCO2e</p>
                    <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-600">
                      {(p.anomaly_flags||[]).length} flag{(p.anomaly_flags||[]).length > 1 ? "s" : ""}
                    </span>
                  </div>
                  {(p.anomaly_flags || []).map((a, i) => {
                    const sc = SEV_COLORS[a.severity] || SEV_COLORS.low;
                    return (
                      <div key={i} className="px-4 py-2.5 border-b border-slate-50">
                        <div className="flex items-start gap-2">
                          <span className={`text-xs font-bold px-1.5 py-0.5 rounded mt-0.5 ${sc}`}>
                            {a.severity?.toUpperCase()}
                          </span>
                          <div>
                            <p className="text-xs font-bold text-slate-700">{a.code}</p>
                            <p className="text-xs text-slate-500 mt-0.5">{a.description}</p>
                            <p className="text-xs text-slate-400 italic mt-0.5">{a.detail}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))
            )}
          </div>
        )}

        {view === "target" && (
          <div className="bg-white border border-slate-200 rounded-xl p-6 text-center">
            <Target size={24} className="text-teal-400 mx-auto mb-3" />
            <p className="text-sm font-semibold text-slate-900 mb-2">Set Annual Target</p>
            <p className="text-xs text-slate-400 mb-4">Set your annual carbon target in the Monthly Inputs page. Track progress automatically.</p>
          <CTAButton variant="action" onClick={() => navigate("/sme/monthly/inputs")}>
              Go to Monthly Inputs →
            </CTAButton>
          </div>
        )}

        <p className="text-xs text-slate-400 italic text-center mt-5 pb-2">
          DCI-SME-PERIODIC-V1.0 · GHG Protocol Corporate Standard · Scope 1/2/3 · Self-reported data · Not third-party verified
        </p>
    </div>
  );
}
